/**
 * Frontend view of the API payload types.
 *
 * Wire-payload types live in @privisy/api-types (single declaration site,
 * shared with the backend). Only frontend-only shapes are declared here.
 */

import type { ComplianceCheck, ComplianceStatus, CreditTransaction, PrivacyPolicyAnalysis, Tier } from '@privisy/api-types';
import { gradeFor } from '../designTokens';






/** True for the recurring-plan tiers. */
export function isSubscriptionTier(tier: Tier): boolean {
  return tier === 'monitor' || tier === 'agency';
}

export type * from '@privisy/api-types';

export const EXPECTED_SCAN_RESULT_SCHEMA_VERSION = 3;

/**
 * Convert a numeric score (0-100) to a letter grade.
 * Uses traditional academic breakpoints:
 * A: 90-100, B: 80-89, C: 70-79, D: 60-69, F: <60
 */
export function getLetterGrade(score: number): string {
  return gradeFor(score).letter;
}



export interface PolicyAnalysis {
  id: string;
  companyName?: string;
  policyUrl?: string;
  analysis: PrivacyPolicyAnalysis;
  checks: ComplianceCheck[];
  score: number;
  overallStatus: ComplianceStatus;
  createdAt: string;
}

export interface PolicyAnalysisAdminSummary {
  id: string;
  companyName?: string;
  policyUrl?: string;
  policyDomain?: string;
  requestSource: 'url' | 'text';
  score: number;
  overallStatus: ComplianceStatus;
  createdAt: string;
}

export interface PolicyAnalysisAdminRecord extends PolicyAnalysisAdminSummary {
  analysis: PrivacyPolicyAnalysis;
  checks: ComplianceCheck[];
}

// ---------------------------------------------------------------------------
// Admin User Management
// ---------------------------------------------------------------------------


export interface AdminUserSummary {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  creditBalance: number | null;
  tier: Tier;
  /** Env-var bypass or admin — credit checks never apply. */
  unlimited: boolean;
}

export interface AdminUserDetails extends AdminUserSummary {
  transactions: CreditTransaction[];
}

/** POST /api/admin/users/:userId/credits — an admin comp, always a `free_grant`. */
export interface AdminCreditGrantRequest {
  /** Integer 1–100 (MAX_ADMIN_CREDIT_GRANT in the backend route). */
  amount: number;
  reason: string;
}

export interface AdminCreditGrantResponse {
  userId: string;
  granted: number;
  creditBalance: number;
}
